import React, {useState} from 'react';
import {CButton, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CTextarea} from '@coreui/react';
import {EventApi} from '@fullcalendar/common';
import {getApi} from "../../apis/backendApis";
import useAlert from "../../services/useAlert";
import useLoading from "../../services/useLoading";
import {dataBr, dataHoraApi, erroDaApi, quandoEvento} from "./modelo";

/**
 * O que foi arrastado no calendario (#484909): um evento (nova data, hora e
 * duracao) ou o prazo de um item. `desfazer` volta o calendario se a mudanca
 * nao for confirmada ou a API recusar.
 */
export type PedidoRemarcacao =
    | {tipo: 'evento', id: number, titulo: string, de: string, deFim: string | null, para: string, paraFim: string | null, desfazer: () => void}
    | {tipo: 'prazo', id: number, titulo: string, de: string, para: string, desfazer: () => void};

/** Monta o pedido a partir do que o FullCalendar devolve no eventDrop/eventResize. */
export const pedidoDoCalendario = (evento: EventApi, antigo: EventApi, desfazer: () => void): PedidoRemarcacao | null => {
    const dados = evento.extendedProps as any;
    if (dados.evento && evento.start) {
        const original = dados.evento as {id: number, titulo: string, inicio: string, fim: string | null};
        let para = dataHoraApi(evento.start);
        let paraFim = evento.end ? dataHoraApi(evento.end) : null;
        // Solto na faixa "dia todo" da semana: muda so o dia, mantem o horario.
        if (evento.allDay) {
            para = `${para.slice(0, 10)} ${original.inicio.slice(11, 16)}`;
            paraFim = original.fim ? `${para.slice(0, 10)} ${original.fim.slice(11, 16)}` : null;
        }
        return {tipo: 'evento', id: original.id, titulo: original.titulo, desfazer, de: original.inicio, deFim: original.fim, para, paraFim};
    }
    if (dados.itemId && evento.start && antigo.start) {
        return {
            tipo: 'prazo', id: dados.itemId, titulo: dados.titulo, desfazer,
            de: dataHoraApi(antigo.start).slice(0, 10), para: dataHoraApi(evento.start).slice(0, 10),
        };
    }
    return null;
};

const faixa = (inicio: string, fim: string | null) => `${quandoEvento(inicio)}${fim ? `–${fim.slice(11, 16)}` : ''}`;

interface Props {
    pedido: PedidoRemarcacao,
    /** feito: a API aceitou (recarregue a tela). */
    aoFechar: (feito: boolean) => void,
}

/**
 * Confirma a remarcacao feita arrastando: o evento pede motivo opcional (vai
 * para o historico) e avisa conflito e prazo do item; o prazo do item pede
 * motivo obrigatorio, como no painel do item.
 */
const Remarcacao = ({pedido, aoFechar}: Props) => {
    const [showAlert] = useAlert();
    const [setLoading] = useLoading();
    const [motivo, setMotivo] = useState('');
    const prazo = pedido.tipo === 'prazo';

    const cancelar = () => {
        pedido.desfazer();
        aoFechar(false);
    };

    const confirmar = () => {
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(pedido.tipo === 'evento'
            ? {inicio: pedido.para, fim: pedido.paraFim, motivo}
            : {prazo: pedido.para, motivo}));
        setLoading(true);
        getApi().post(pedido.tipo === 'evento' ? `processo/evento/remarcar/${pedido.id}` : `processo/item/reprogramar/${pedido.id}`, corpo)
            .then(resposta => {
                showAlert({msg: resposta.data.msg ?? 'Feito.', type: 'success', toaster: true});
                if (resposta.data.avisos?.length) {
                    showAlert({title: 'Remarcado, mas atenção', msg: resposta.data.avisos.join(' '), type: 'warning', toaster: true});
                }
                aoFechar(true);
            })
            .catch(erro => {
                pedido.desfazer();
                showAlert({title: 'Não foi possível', msg: erroDaApi(erro, 'Erro ao remarcar.'), type: 'danger', toaster: true});
                aoFechar(false);
            })
            .finally(() => setLoading(false));
    };

    return (
        <CModal show onClose={cancelar} centered>
            <CModalHeader closeButton>
                <CModalTitle>{prazo ? 'Reprogramar o prazo do item' : 'Remarcar evento'}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                <p className="pr-remarcacao-texto"><strong>“{pedido.titulo}”</strong></p>
                <p className="pr-remarcacao-de-para">
                    <span>{pedido.tipo === 'evento' ? faixa(pedido.de, pedido.deFim) : dataBr(pedido.de)}</span>
                    <span aria-hidden>→</span>
                    <strong>{pedido.tipo === 'evento' ? faixa(pedido.para, pedido.paraFim) : dataBr(pedido.para)}</strong>
                </p>
                {prazo && <p className="pr-ajuda">Fim de semana e feriado passam para o próximo dia útil.</p>}
                <CTextarea rows={2} value={motivo} onChange={(e: any) => setMotivo(e.target.value)}
                           placeholder={prazo ? 'Motivo (obrigatório)' : 'Motivo (opcional, vai para o histórico)'} aria-label="Motivo"/>
            </CModalBody>
            <CModalFooter>
                <CButton color="dark" variant="outline" onClick={cancelar}>Cancelar</CButton>
                <CButton color="primary" disabled={prazo && motivo.trim().length < 5} onClick={confirmar}>Confirmar</CButton>
            </CModalFooter>
        </CModal>
    );
};

export default Remarcacao;
